import {
  Box,
  Button,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import { IconChevronDownSmall } from 'central-icons/IconChevronDownSmall';
import { truncate } from 'lodash';
import { type ComponentProps, useEffect } from 'react';

import { FeatureFlag } from '@revelio/core';

import { PopoutTree } from '../../../../shared/components/tree/PopoutTree/PopoutTree';
import { useTreeSelections } from '../../../../shared/components/tree/use-tree-selections';
import { SelectionListControls } from '../../../../shared/selection-lists/components/SelectionListControls/SelectionListControls';
import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import type { SubfilterTree } from '../PageState/types';
import styles from './Subfilter.module.css';

export const Subfilter = ({
  subfilterList,
  selectedItems,
  onSubmit,
  openByDefault,
  ...props
}: {
  subfilterList: SubfilterTree[];
  selectedItems: SubfilterTree[];
  openByDefault?: boolean;
  onSubmit: (selections: SubfilterTree[]) => void;
} & Omit<ComponentProps<typeof Button>, 'onSubmit'>) => {
  const { isOpen, onClose, onOpen } = useDisclosure();

  const label = (() => {
    if (selectedItems.length === 0) return 'Select a Segment';
    if (selectedItems.length === 1) {
      return truncate(selectedItems[0]?.name ?? '', {
        length: 20,
        omission: '...',
      });
    }

    return `${selectedItems.length} selected`;
  })();

  return (
    <Popover
      placement="bottom"
      isOpen={isOpen}
      onClose={onClose}
      isLazy
      lazyBehavior="unmount"
    >
      <PopoverTrigger>
        <Button
          data-testid="plot-sub-filter-trigger"
          className={styles.trigger}
          variant="outline"
          colorScheme="gray"
          width="120px"
          justifyContent="space-between"
          color="text.primary"
          fontWeight="normal"
          aria-label="Plot Sub-filter"
          size="xs"
          fontSize="10px"
          rightIcon={<Icon boxSize={3} as={IconChevronDownSmall} />}
          textOverflow="ellipsis"
          onClick={onOpen}
          position="absolute"
          {...props}
        >
          {label}
        </Button>
      </PopoverTrigger>
      {isOpen && (
        <Portal>
          <PopoverContent>
            <PopoverArrow />
            <PopoverBody>
              <SubfilterTreeSelection
                data={subfilterList}
                selectedItems={selectedItems}
                onClose={onClose}
                onSubmit={onSubmit}
                openByDefault={openByDefault}
              />
            </PopoverBody>
          </PopoverContent>
        </Portal>
      )}
    </Popover>
  );
};

const SubfilterTreeSelection = ({
  data,
  selectedItems,
  onClose,
  onSubmit,
  openByDefault,
}: {
  data: SubfilterTree[];
  selectedItems: SubfilterTree[];
  onClose?: () => void;
  onSubmit: (selections: SubfilterTree[]) => void;
  openByDefault?: boolean;
}) => {
  const isSelectionListIdsEnabled = useUnleashFlag(
    FeatureFlag.SelectionListIds
  );
  const { selections, onChange, onClear } = useTreeSelections({
    initialSelections: selectedItems.map((item) => item.id),
  });

  const hasTreeStructure = data.some(
    (item) => item.children && item.children.length > 0
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (selectedItems.every((item) => selections.includes(item.id))) return;
    onChange(selectedItems.map((item) => item.id));
  }, [selectedItems]);

  const handleSubmit = () => {
    if (hasTreeStructure) {
      const flatData = data.flatMap((item) => [item, ...(item.children ?? [])]);
      const selectionItems = flatData.filter((item) =>
        selections.includes(item.id)
      );
      onSubmit(selectionItems);
      onClose?.();
    } else {
      const selectionItems = data.filter((item) =>
        selections.includes(item.id)
      );
      onSubmit(selectionItems);
      onClose?.();
    }
  };

  return (
    <Box data-testid="plot-sub-filter-tree">
      <PopoutTree
        data={data}
        value={selections}
        onChange={onChange}
        openByDefault={openByDefault}
        skipDefaultSort={!hasTreeStructure}
        showCollapseToggle={false}
        isSelectionListIdsEnabled={isSelectionListIdsEnabled}
        {...(openByDefault && { disabledLevels: [0] })}
      />
      <SelectionListControls
        className={styles.selectionListControls}
        onClear={onClear}
        onClose={onClose}
        onSubmit={handleSubmit}
        submitText="Apply"
      />
    </Box>
  );
};
